/* eslint-disable no-extra-boolean-cast */
import { Site } from '@ps/web-core-enums';
import { Currency } from '@ps/web-core-store';
import type { Feature, FeatureFlag } from '@ps/web-core-store';
import { format, isMatch } from 'date-fns';
import { isBoolean, memoize, omit } from 'lodash';
import type { FormatNumberOptions } from 'react-intl';
import type { Observable } from 'rxjs';
import { EMPTY, of } from 'rxjs';
import { expand, filter } from 'rxjs/operators';

import { request } from '@swf/core/api/request';
import { Country } from '@swf/core/enums';
import type { FormatAsCurrency } from '@swf/core/hooks/useCurrencyFormatter';
import type { Store } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { Error } from '@swf/core/types/Response';
import type { WizardContext } from '@swf/core/types/Wizard';
import type { DepositPayload, Rates } from '@swf/first';

import {
  creditTimeDays,
  creditTimeHours,
  creditTimeInstant,
  creditTimeMinutes,
  creditTimeTwoDays
} from '../components/Deposit/data';
import type {
  CashoutOptionRate,
  Fields,
  InovapayBankInfo,
  PaySystem,
  PaySystems,
  PreCashinInfo,
  PsPayBank,
  SavedCard,
  SavedCards
} from '../types/Cashier';
import { CardType } from '../types/cardType';
import { DepositStatus, TransactionStatus } from '../types/status';

import {
  TXTCLI_Incorrect_Information_Entered,
  TXTCLI_Please_double_check_and_try_to_ELL,
  TXTCLI_Please_specify_3_digit_institu_ELL,
  TXTCLI_Please_specify_5_digit_branch__ELL,
  TXTCLI_Social_Insurance_NumberX_wrong_ELL,
  TXTCLI_Street_address_must_be_at_leas_ELL,
  TXTCLI_The_IBAN_you_have_entered_is_i_ELL,
  TXTCLI_The_SIN_you_entered_does_not_m_ELL,
  TXTCLI_The_bank_routing_number_you_pr_ELL,
  TXTCLI_The_information_entered_in_the_ELL,
  TXTCLI_The_password_you_have_entered__ELL,
  TXTCLI_The_street_address_entered_app_ELL,
  TXTCLI_The_voucher_code_you_have_ente_ELL,
  TXTCLI_Wrong_account_number_format,
  TXTWCL_24_Hour_Deposit,
  TXTWCL_48_Hour_Deposit,
  TXTWCL_4_5_Day_Deposit,
  TXTWCL_Ensure_your_CNP_matches_your_d_ELL,
  TXTWCL_Instant_Deposit,
  TXTWCL_Invalid_CNPX_Please_try_againX,
  TXTWCL_Please_enter_a_valid_middle_na_ELL,
  TXTWCL_The_card_number_entered_is_not_ELL,
  TXTWCL_This_can_take_up_to_5_minutes__ELL,
  TXTWCL_Your_CNP_must_be_13_digitsX
} from './cashier.messages.json';

export const conversionRateBase = 100000000;

export const dollarsToCents = (dollars: number): number => Number((dollars * 100).toFixed());

export const trimWithoutRounding = (value: number, decimalDigits = 2): number => {
  const numberToString = value.toString();

  return numberToString.indexOf('.') > -1
    ? Number(numberToString.substring(0, numberToString.indexOf('.') + (decimalDigits + 1)))
    : value;
};

export const convertAmountByRate = (
  amount: number,
  rate: number,
  currency: Currency,
  inverted = false,
  decimalDigits?: number
): number => {
  const trimmedAmount = trimWithoutRounding(amount);
  const result = Number(
    inverted
      ? (trimmedAmount * conversionRateBase) / rate
      : (trimmedAmount * rate) / conversionRateBase
  );

  return isZeroExponentCurrency(currency)
    ? Math.ceil(result)
    : trimWithoutRounding(result, decimalDigits);
};

export const formatExpiryDate = (expDate: number | undefined) => {
  if (expDate) {
    return isMatch(expDate.toString(), 'yyyyMM') ? expDate.toString() : format(expDate, 'yyyyMM');
  }
};

export const getCurrencyRate = (rates: Rates | CashoutOptionRate[], currency: Currency) => {
  const currencyRate = rates.find(({ curr }) => curr === currency);
  return currencyRate?.rate || -1;
};

export const sanitizeDeposit = (payload: DepositPayload) =>
  omit(payload, ['accountNumber', 'cvn', 'expiryDate']);

export const getPaySystemName = (paySystems: PaySystems, type: CardType) => {
  let paySystemName = '';
  const paySystem = paySystems.find(
    ({ cardType }: { cardType: CardType }) => cardType === type
  ) as PaySystem;
  if (paySystem) {
    const { i18nSystemName = '' } = paySystem;
    paySystemName = i18nSystemName;
  }
  return paySystemName;
};

export const getAsPayBankName = (asPaybankInfo: PsPayBank[], selectedBankId: number) => {
  let asPayBankName = '';
  let asPayBankAccountNumber = '';
  const bank = asPaybankInfo.find(({ bankId }: { bankId: number }) => bankId === selectedBankId);
  if (bank) {
    const { bankName = '', bankAccount } = bank;
    asPayBankName = bankName;
    asPayBankAccountNumber = bankAccount;
  }
  return { asPayBankName, asPayBankAccountNumber };
};

export const getBankDetails = (
  selectedProductId: string,
  allowedSubsystems?: InovapayBankInfo[],
  savedMethod?: SavedCard
) => {
  const bankName = allowedSubsystems?.find(
    ({ productId }) => productId === selectedProductId
  )?.description;
  const savedMaskedNumber =
    savedMethod && savedMethod.savedCard.maskedCardNum ? savedMethod.savedCard.maskedCardNum : '';

  return { bankName, savedMaskedNumber };
};

export const getSavedMethod = (savedCards: SavedCards, savedCardInfoId?: number): SavedCard[] =>
  savedCards.filter(({ savedCard: { cardInfoId } }) => cardInfoId === savedCardInfoId);

export const getFeatureFlags = async ({ getState, dispatch }: Store, flags: FeatureFlag[]) => {
  const buffer: FeatureFlag[] = [];
  const existingFlags: Partial<Record<FeatureFlag, boolean>> = {};
  const state = getState();
  const options = select.cms.getRequestOptions(state);

  flags.forEach((flag) => {
    const value = select.feature.check(state, flag);

    if (isBoolean(value)) {
      buffer.push(flag);
      existingFlags[flag] = value;
    }
  });

  const requiredFlags = flags.filter((flag) => !buffer.includes(flag));

  if (requiredFlags.length) {
    const { body: features } = await request<Feature>({
      ...options,
      service: 'features.check',
      body: { features: requiredFlags }
    });
    dispatch.feature.update(features);

    return { ...features, ...existingFlags };
  }

  return existingFlags;
};

export const getDepositStatus = (status: TransactionStatus) => {
  switch (status) {
    case TransactionStatus.APPROVED:
      return DepositStatus.COMPLETED;
    case TransactionStatus.REJECTED:
      return DepositStatus.REJECTED;
    default:
      return DepositStatus.TIMEOUT;
  }
};

export const getRmfRequiredPath = (site: Site, country?: string) => {
  const path = {
    [Site.CAON]: '#rmf.rmf-legal-name',
    [Site.IT]: '#rmf.rmf-real-money-information-check',
    [Country.Brazil]: '#rmf.rmfdob'
  };

  return (country && path[country]) || path[site] || '#rmf.set-crm-bonus';
};

export const mapErrorCodes = (code: Error['code']) => {
  const mappedMessage = {
    TXTCLI_Incorrect_Information_Entered,
    TXTCLI_Please_specify_3_digit_institu_ELL,
    TXTCLI_Please_specify_5_digit_branch__ELL,
    TXTCLI_Social_Insurance_NumberX_wrong_ELL,
    TXTCLI_The_IBAN_you_have_entered_is_i_ELL,
    TXTCLI_The_information_entered_in_the_ELL,
    TXTCLI_The_SIN_you_entered_does_not_m_ELL,
    TXTCLI_The_street_address_entered_app_ELL,
    TXTCLI_The_voucher_code_you_have_ente_ELL,
    TXTCLI_Wrong_account_number_format,
    TXTWCL_Ensure_your_CNP_matches_your_d_ELL,
    TXTWCL_Invalid_CNPX_Please_try_againX,
    TXTWCL_The_card_number_entered_is_not_ELL,
    TXTWCL_Please_enter_a_valid_middle_na_ELL,
    TXTWCL_Your_CNP_must_be_13_digitsX,
    TXTCLI_Please_double_check_and_try_to_ELL,
    TXTCLI_The_password_you_have_entered__ELL,
    TXTCLI_Street_address_must_be_at_leas_ELL,
    TXTCLI_The_bank_routing_number_you_pr_ELL
  };

  return code && mappedMessage[code]?.defaultMessage;
};

export const zeroExponentCurrencies = [
  Currency.ChileanPeso,
  Currency.HungarianForint,
  Currency.JapaneseYen
];

export const isZeroExponentCurrency = memoize((currency: Currency) =>
  zeroExponentCurrencies.includes(currency)
);

export const trimAsterisks = (text: string) => text.replace(/\*{4,}/g, '****');

export const filterPaySystems = (
  paySystems: PaySystems,
  isApplePayAvailable: boolean,
  isGooglePayDepositAvailable: boolean
) =>
  paySystems.filter(({ cardType }) => {
    if (cardType === CardType.ApplePay) {
      return isApplePayAvailable;
    }
    if (cardType === CardType.GooglePay) {
      return isGooglePayDepositAvailable;
    }

    return true;
  });

export const getCreditTime = (type: CardType, country?: Country, depositSuccess?: boolean) => {
  if (type === CardType.PsPayAstropay && country) {
    switch (country) {
      case Country.Peru:
      case Country.Uruguay:
        return TXTWCL_24_Hour_Deposit;
      case Country.Mexico:
      case Country.Brazil:
        return TXTWCL_48_Hour_Deposit;
      case Country.Chile:
        return depositSuccess ? TXTWCL_This_can_take_up_to_5_minutes__ELL : TXTWCL_Instant_Deposit;
    }
  }
  if (creditTimeInstant.includes(type)) {
    return TXTWCL_Instant_Deposit;
  }
  if (creditTimeMinutes.includes(type)) {
    return depositSuccess ? TXTWCL_This_can_take_up_to_5_minutes__ELL : TXTWCL_Instant_Deposit;
  }
  if (creditTimeDays.includes(type)) {
    return TXTWCL_4_5_Day_Deposit;
  }
  if (creditTimeHours.includes(type)) {
    return TXTWCL_24_Hour_Deposit;
  }
  if (creditTimeTwoDays.includes(type)) {
    return TXTWCL_48_Hour_Deposit;
  }
};

export const formatLimitOptions = (
  currency: Currency,
  options: number[],
  formatAsCurrency: FormatAsCurrency
) =>
  options.map((value) => ({
    value,
    text: formatAsCurrency({ value, currency, minimumFractionDigits: 0, cents: true })
  }));

export const executeHistoryPush = (isEmbedded: boolean) => {
  if (!window.history.state?.prevUrl) {
    window.history.pushState(
      { prevUrl: window.location.href },
      document.title,
      window.location.href
    );
  }
  if (isEmbedded) {
    const url = `${window.location.href}/${document.title.toLowerCase().replace(/ /g, '-')}`;
    window.history.pushState({ prevUrl: url }, document.title, url);
  }
};

export const getClosetConvertableAmount = (
  rate: number,
  side: 'left' | 'right',
  sourceCurrency: Currency,
  targetAmount: number,
  targetCurrency: Currency
): Observable<number> => {
  const calculatedSourceAmount = convertAmountByRate(targetAmount, rate, targetCurrency, true, 2);
  if (side === 'left') {
    const condition = (res: number) =>
      convertAmountByRate(res, rate, sourceCurrency, false, 2) <= +targetAmount;

    return of(calculatedSourceAmount).pipe(
      expand((val) => (condition(val) ? EMPTY : of(+(val - 0.01).toFixed(2)))),
      filter(condition)
    );
  } else {
    const condition = (res: number) =>
      convertAmountByRate(res, rate, sourceCurrency, false, 2) >= +targetAmount;

    return of(calculatedSourceAmount).pipe(
      expand((val) => (condition(val) ? EMPTY : of(+(val + 0.01).toFixed(2)))),
      filter(condition)
    );
  }
};

export const getConversionRate = (rates: Rates, currency: Currency) =>
  rates.reduce((result, { curr, rate }) => {
    if (curr === currency) {
      result = rate;
    }
    return result;
  }, conversionRateBase);

export const isRedirectStatus = (status: DepositStatus) =>
  [
    DepositStatus.REDIRECT,
    DepositStatus.REJECTED_X3D_SECURE2_REDIRECT,
    DepositStatus.REJECTED_X3D_SECURE_REQUIRED_ERROR,
    DepositStatus.REJECTED_X3D_SECURE2_REQUIRED_ERROR
  ].includes(status);

export const updateFormFieldsValues = (
  values: Pick<WizardContext, 'values'>,
  fieldsData: Fields = []
) => {
  fieldsData.forEach(({ name, value }) => {
    if (!!value || value === 0) {
      values[name] = value;
    }
  });
};

export const calculateCashBonusAmount = (
  preCashinInfo: PreCashinInfo | null,
  formatNumber: (value: number | bigint, opts?: FormatNumberOptions | undefined) => string
): string | undefined => {
  if (!preCashinInfo) return;

  const {
    fppToExercise,
    bonusMax,
    bonusPercent,
    potentialBonusAmount,
    instantBonusInfo,
    bonusCurrency
  } = preCashinInfo;

  if (!instantBonusInfo && fppToExercise === 0 && !!(bonusMax || bonusPercent)) {
    return formatNumber(Math.min(bonusMax, potentialBonusAmount), {
      currency: bonusCurrency,
      style: 'currency',
      currencyDisplay: 'code',
      maximumFractionDigits: 2,
      minimumFractionDigits: 2
    });
  }

  return;
};
